﻿// SiteViewport3D.tsx — Multi-elevation 3D chain renderer
// Computes world positions + rotations for all checked elevations by walking
// the connected-elevation graph, then delegates to SiteViewer3D for rendering.

import { useMemo } from 'react';
import type { Elevation, PanelData, ClashResult, Floor } from '../types/panelvar';
import { cornerJointWidth } from '../types/panelvar';
import { computeFootprint, wallWidth, DIR_ANGLE } from '../utils/buildingFootprint';
import { detectClashes } from '../utils/clashDetection';
import SiteViewer3D from './SiteViewer3D';

export interface WallSegment {
  elevId: string;
  name: string;
  panels: PanelData[];
  /** World X of wall origin */
  x: number;
  /** World Z of wall origin */
  z: number;
  /** Y-axis rotation in radians (0 = along +X) */
  rotationY: number;
  leftEndJoint: number;
  rightEndJoint: number;
  /** Y offset in inches for multi-floor rendering (0 = ground level) */
  baseElevation?: number;
  /** Floor ID this segment belongs to */
  floorId?: string;
}

interface Props {
  elevations: Elevation[];
  panels: PanelData[];
  jointWidth?: number;
  highlightId?: string | null;
  selectedPanelIds?: string[];
  onPanelClick?: (panelId: string, ctrlKey: boolean) => void;
  onPanelDoubleClick?: (panelId: string) => void;
  /** All floors — when provided, renders all floors stacked vertically */
  allFloors?: Floor[];
  /** Full elevation pool for multi-floor (required when allFloors is set) */
  allElevations?: Elevation[];
  /** Global checked elevation IDs — upper floors only render elevations in this set */
  checkedElevIds?: Set<string>;
}

/** Compute the Y base elevation (inches) for each floor by summing prior floors' max panel heights + slab thicknesses */
function computeFloorBaseElevations(floors: Floor[], allElevations: Elevation[], panels: PanelData[]): Map<string, number> {
  const result = new Map<string, number>();
  let y = 0;
  for (const floor of floors) {
    if (floor.type === 'mezzanine') {
      result.set(floor.id, floor.ledgerHeight ?? y);
      continue;
    }
    result.set(floor.id, y);
    const floorElevs = allElevations.filter(e => floor.elevationIds.includes(e.id));
    const floorPanelIds = new Set(floorElevs.flatMap(e => e.panelIds));
    const maxH = Math.max(0, ...panels.filter(p => floorPanelIds.has(p.id)).map(p => p.mpvar.height));
    if (maxH > 0) y += maxH + (floor.slabThickness ?? 5);
  }
  return result;
}

export default function SiteViewport3D({ elevations, panels, jointWidth = 0.75, selectedPanelIds, onPanelClick, onPanelDoubleClick, allFloors, allElevations, checkedElevIds }: Props) {
  const { segments, isClosed, corners } = useMemo(() => {
    if (elevations.length === 0) return { segments: [] as WallSegment[], isClosed: false, corners: [] as {x:number;y:number}[] };

    // Use the shared footprint utility for the chain walk
    const footprint = computeFootprint(elevations, panels, jointWidth);

    // Map WallSegment2D → WallSegment (3D): 2D-Y maps to 3D-Z
    const result: WallSegment[] = footprint.walls.map(w => ({
      elevId: w.elevId,
      name: w.name,
      panels: w.panels,
      x: w.startX,
      z: w.startY,
      rotationY: w.angle,
      leftEndJoint: w.leftEndJoint,
      rightEndJoint: w.rightEndJoint,
    }));

    const visited = new Set(footprint.walls.map(w => w.elevId));

    // Any checked elevations NOT in the chain — render standalone
    // Compute chain bounding box for auto-positioning fallback
    let chainMinX = 0, chainMaxX = 0, chainMinZ = 0, chainMaxZ = 0;
    for (const w of footprint.walls) {
      chainMinX = Math.min(chainMinX, w.startX, w.endX);
      chainMaxX = Math.max(chainMaxX, w.startX, w.endX);
      chainMinZ = Math.min(chainMinZ, w.startY, w.endY);
      chainMaxZ = Math.max(chainMaxZ, w.startY, w.endY);
    }
    const chainMidX = (chainMinX + chainMaxX) / 2;
    const chainMidZ = (chainMinZ + chainMaxZ) / 2;
    let standaloneOffset = 0;
    const gap = 240; // 20' separation from chain

    const resolvePanels = (elev: Elevation): PanelData[] =>
      elev.panelIds.map(id => panels.find(p => p.id === id)).filter((p): p is PanelData => p != null);

    for (const e of elevations) {
      if (!visited.has(e.id)) {
        const ep = resolvePanels(e);
        const lj = cornerJointWidth(e.leftCorner ?? 'continuous', jointWidth);
        const rj = cornerJointWidth(e.rightCorner ?? 'continuous', jointWidth);
        const ww = wallWidth(ep, jointWidth, lj, rj);

        // Use explicit world position if available; otherwise auto-position
        let sx: number, sz: number, srot: number;
        if (e.worldX !== undefined && e.worldZ !== undefined) {
          sx = e.worldX;
          sz = e.worldZ;
          srot = e.worldRotation ?? (DIR_ANGLE[e.direction] ?? 0);
        } else {
          srot = DIR_ANGLE[e.direction] ?? 0;
          switch (e.direction) {
            case 'R': sx = chainMaxX + gap; sz = chainMidZ - ww / 2; break;
            case 'L': sx = chainMinX - gap; sz = chainMidZ + ww / 2; break;
            case 'B': sx = chainMidX - ww / 2; sz = chainMinZ - gap; break;
            case 'F': sx = chainMidX + ww / 2; sz = chainMaxZ + gap; break;
            default:  sx = chainMaxX + gap + standaloneOffset; sz = chainMidZ; break;
          }
          standaloneOffset += ww + gap;
        }

        result.push({
          elevId: e.id,
          name: e.name,
          panels: ep,
          x: sx,
          z: sz,
          rotationY: srot,
          leftEndJoint: lj,
          rightEndJoint: rj,
        });
      }
    }

    return { segments: result, isClosed: footprint.isClosed, corners: footprint.corners };
  }, [elevations, panels, jointWidth]);

  // ── Multi-floor: compute segments for all other floors, offset by baseElevation ─
  const upperFloorSegments = useMemo(() => {
    if (!allFloors || !allElevations || allFloors.length <= 1) return [] as WallSegment[];
    const baseMap = computeFloorBaseElevations(allFloors, allElevations, panels);
    const result: WallSegment[] = [];
    // Skip floor 0 (already rendered by the primary segments above)
    for (let fi = 1; fi < allFloors.length; fi++) {
      const floor = allFloors[fi];
      const baseElevation = baseMap.get(floor.id) ?? 0;
      const floorElevs = allElevations.filter(e =>
        floor.elevationIds.includes(e.id) && (!checkedElevIds || checkedElevIds.has(e.id))
      );
      if (floorElevs.length === 0) continue;
      const fp = computeFootprint(floorElevs, panels, jointWidth);
      for (const w of fp.walls) {
        result.push({ elevId: w.elevId, name: w.name, panels: w.panels, x: w.startX, z: w.startY, rotationY: w.angle, leftEndJoint: w.leftEndJoint, rightEndJoint: w.rightEndJoint, baseElevation, floorId: floor.id });
      }
      // Standalone elevations for this floor
      const visited = new Set(fp.walls.map(w => w.elevId));
      for (const e of floorElevs) {
        if (visited.has(e.id)) continue;
        const ep = e.panelIds.map(id => panels.find(p => p.id === id)).filter((p): p is PanelData => p != null);
        const lj = cornerJointWidth(e.leftCorner ?? 'continuous', jointWidth);
        const rj = cornerJointWidth(e.rightCorner ?? 'continuous', jointWidth);
        const srot = DIR_ANGLE[e.direction] ?? 0;
        result.push({ elevId: e.id, name: e.name, panels: ep, x: e.worldX ?? 0, z: e.worldZ ?? 0, rotationY: srot, leftEndJoint: lj, rightEndJoint: rj, baseElevation, floorId: floor.id });
      }
    }
    return result;
  }, [allFloors, allElevations, panels, jointWidth, checkedElevIds]);

  // Floor polygon: use closure-corrected corners from the footprint.
  const floorVertices = useMemo(() => {
    if (!isClosed || !corners || corners.length < 3) return undefined;
    return corners.map(c => [c.x, c.y] as [number, number]);
  }, [corners, isClosed]);

  // ── Inter-story slabs + roof for multi-floor buildings ───────────────────
  const { upperSlabHeights, roofHeight } = useMemo(() => {
    if (!allFloors || !allElevations || allFloors.length <= 1) return { upperSlabHeights: [] as number[], roofHeight: undefined as number | undefined };
    const baseMap = computeFloorBaseElevations(allFloors, allElevations, panels);

    // Inter-story slabs: Y = base of each floor above ground
    const upperSlabHeights: number[] = [];
    for (let fi = 1; fi < allFloors.length; fi++) {
      upperSlabHeights.push(baseMap.get(allFloors[fi].id) ?? 0);
    }

    // Roof: at the roof line of the last floor's panels (roofLine = plate height from panel bottom)
    // Falls back to panel height if no roofLine is set.
    const lastFloor = allFloors[allFloors.length - 1];
    const lastBase  = baseMap.get(lastFloor.id) ?? 0;
    const lastPanelIds = new Set(
      allElevations.filter(e => lastFloor.elevationIds.includes(e.id)).flatMap(e => e.panelIds)
    );
    const lastFloorPanels = panels.filter(p => lastPanelIds.has(p.id));
    const lastRoofLine = Math.max(0, ...lastFloorPanels.map(p => p.mpvar.roofLine ?? p.mpvar.height));
    const roofHeight = lastRoofLine > 0 ? lastBase + lastRoofLine : undefined;

    return { upperSlabHeights, roofHeight };
  }, [allFloors, allElevations, panels]);

  // ── Clash detection ──────────────────────────────────────────────────────
  const clashes: ClashResult[] = useMemo(() => {
    if (segments.length === 0) return [];
    const footprint = computeFootprint(elevations, panels, jointWidth);
    return detectClashes(footprint, segments, jointWidth);
  }, [segments, elevations, panels, jointWidth]);

  if (elevations.length === 0) {
    return <div className="site-viewport-empty"><p>Check elevations in the sidebar to view 3D</p></div>;
  }

  const totalPanels = segments.reduce((s, seg) => s + seg.panels.length, 0);
  if (totalPanels === 0) {
    return (
      <div className="site-viewport-empty">
        <p>No panels assigned to checked elevations</p>
        <p className="site-viewport-hint">Assign panels from the properties panel on the right</p>
      </div>
    );
  }

  const allSegments = [...segments, ...upperFloorSegments];
  const allElevationsForViewer = allElevations ?? elevations;
  return (
    <div className="site-viewport-3d">
      <div className="site-viewport-label">
        {allSegments.length} elevation{allSegments.length !== 1 ? 's' : ''} · {allSegments.reduce((s, seg) => s + seg.panels.length, 0)} panel{totalPanels !== 1 ? 's' : ''}
        {upperFloorSegments.length > 0 && ` · ${(allFloors?.length ?? 1)} floors`}
      </div>
      <SiteViewer3D segments={allSegments} elevations={allElevationsForViewer} jointWidth={jointWidth} floorVertices={floorVertices} upperSlabHeights={upperSlabHeights} roofHeight={roofHeight} selectedPanelIds={selectedPanelIds} onPanelClick={onPanelClick} onPanelDoubleClick={onPanelDoubleClick} clashes={clashes} />
    </div>
  );
}
